import { makeAutoObservable, runInAction } from "mobx";
import { track } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import type { IWorkspaceExportData } from "@/types/workspace";
import type { IWorkspaceExportRequest } from "@/services/workspace-export.service";

export class WorkspaceExportsStore {
  history: IWorkspaceExportData[] = [];
  count = 0;
  nextCursor = "";
  prevCursor = "";
  hasNextPage = false;
  hasPrevPage = false;
  currentCursor = "10:0:0";
  isLoading = false;
  isSubmitting = false;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  get hasProcessingExports(): boolean {
    return this.history.some((item) => item.status === "processing");
  }

  async fetchHistory(slug: string, cursor?: string, perPage = 10): Promise<IWorkspaceExportData[]> {
    const nextCursor = cursor && cursor.length > 0 ? cursor : `${perPage}:0:0`;
    this.isLoading = true;
    try {
      const response = await this.rootStore.workspaceExportService.listExports(slug, {
        cursor: nextCursor,
        per_page: perPage,
      });

      runInAction(() => {
        this.history = response.results;
        this.count = response.count;
        this.nextCursor = response.next_cursor;
        this.prevCursor = response.prev_cursor;
        this.hasNextPage = response.next_page_results;
        this.hasPrevPage = response.prev_page_results;
        this.currentCursor = nextCursor;
      });

      return response.results;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async createExport(
    slug: string,
    payload: IWorkspaceExportRequest,
  ): Promise<IWorkspaceExportData> {
    this.isSubmitting = true;
    try {
      const result = await this.rootStore.workspaceExportService.createExport(slug, payload);
      // The export is a background job the history lists, so it counts as made
      // once the server has queued it (NG-2062).
      track("export_created", {
        export_type: "workspace_export",
        export_id: result.id,
        format: payload.provider,
        mode: "async",
        project_count: payload.project.length,
        source: "page",
      });
      await this.fetchHistory(slug);
      return result;
    } finally {
      runInAction(() => {
        this.isSubmitting = false;
      });
    }
  }
}
